import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';

export const useSession = () => {
  const [sessionId, setSessionId] = useState<string>('');
  const { user } = useAuth();

  // Use authenticated user ID or fallback to localStorage
  const userId = user?.userId || localStorage.getItem('fallbackUserId') || '';

  // Generate a unique session ID
  const generateSessionId = useCallback(() => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substr(2, 9);
    const userPrefix = userId ? `${userId.substring(0, 8)}_` : '';
    return `session_${userPrefix}${timestamp}_${random}`;
  }, [userId]);

  // Generate a fallback user ID for development/testing
  const generateFallbackUserId = useCallback(() => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substr(2, 9);
    return `fallback_user_${timestamp}_${random}`;
  }, []);

  // Create new session
  const createNewSession = useCallback(() => {
    const newSessionId = generateSessionId();
    const timestamp = Date.now().toString();
    setSessionId(newSessionId);
    localStorage.setItem('currentSessionId', newSessionId);
    localStorage.setItem('sessionTimestamp', timestamp);
    return newSessionId;
  }, [generateSessionId]);

  // Initialize session on mount
  useEffect(() => {
    // Ensure we have a user ID (authenticated or fallback)
    if (!userId) {
      const fallbackUserId = generateFallbackUserId();
      localStorage.setItem('fallbackUserId', fallbackUserId);
    }

    // Check if there's an existing session that's not too old (max 24 hours)
    const existingSessionId = localStorage.getItem('currentSessionId');
    const sessionTimestamp = localStorage.getItem('sessionTimestamp');
    const now = Date.now();
    const sessionAge = sessionTimestamp ? now - parseInt(sessionTimestamp) : Infinity;
    const maxSessionAge = 24 * 60 * 60 * 1000; // 24 hours
    
    if (existingSessionId && sessionAge < maxSessionAge) {
      setSessionId(existingSessionId);
    } else {
      // Session is too old or doesn't exist, create a new one
      createNewSession();
    }
  }, [userId, createNewSession, generateFallbackUserId]);

  // Create new session when user changes (login/logout)
  useEffect(() => {
    if (user?.userId) {
      // User authenticated, create new session
      createNewSession();
    }
  }, [user?.userId, createNewSession]);

  return {
    sessionId,
    userId: userId || localStorage.getItem('fallbackUserId') || '',
    createNewSession,
  };
}; 